import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { CheckCircleIcon as CheckCircle, SpinnerIcon as Spinner, WarningIcon as Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function EscrowReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling");
  const [escrowId, setEscrowId] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        // Silent: this page has its own visible "polling" status pill,
        // so we don't want the global progress bar flashing every 1.5s.
        const r = await api.get(`/billing/checkout-status/${sessionId}`, { silent: true });
        if (r.data?.payment_status === "paid") {
          setStatus("ok");
          setEscrowId(r.data?.escrow_id);
          return;
        }
        if (r.data?.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {
        /* ignore — keep polling */
      }
      if (attempts >= 8) {
        setStatus("timeout");
        return;
      }
      setTimeout(tick, 1500);
    };
    tick();
  }, [sessionId]);

  return (
    <AppShell>
      <div className="max-w-xl mx-auto pt-12">
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-10 text-center" data-testid="escrow-return-card">
          {status === "polling" && (
            <>
              <Spinner size={40} className="mx-auto mb-4 animate-spin text-ink" />
              <h1 className="font-spectral text-2xl tracking-tight">Confirming payment…</h1>
              <p className="text-sm text-[#6B6B6B] mt-2">This usually takes a couple seconds.</p>
            </>
          )}
          {status === "ok" && (
            <>
              <CheckCircle size={48} weight="fill" className="mx-auto mb-4 text-green-600" />
              <h1 className="font-spectral text-3xl tracking-tight">Payment received!</h1>
              <p className="text-[#6B6B6B] mt-2 mb-6">
                Your funds are now held in escrow. The breeder has been notified to coordinate pickup. When you have the dog, return here to release the funds.
              </p>
              <Button
                onClick={() => navigate("/escrow/my")}
                data-testid="escrow-return-go-btn"
                className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white"
              >
                Go to my purchases
              </Button>
            </>
          )}
          {(status === "expired" || status === "timeout" || status === "error") && (
            <>
              <Warning size={48} weight="duotone" className="mx-auto mb-4 text-yellow-600" />
              <h1 className="font-spectral text-2xl tracking-tight">Payment status unclear</h1>
              <p className="text-[#6B6B6B] mt-2 mb-6">
                We couldn't confirm payment status right away. Check your email or the marketplace listing in a few minutes.
              </p>
              <Button onClick={() => navigate("/marketplace")} className="rounded-full">Back to marketplace</Button>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}
